import * as THREE from 'three';

/**
 * 一个业务显示源的只读拓扑空间。直接读取权威快照和网格数组，不创建 FeatureResultMesh。
 * 普通结果体使用其 geometry；导入 Body 使用已归一到 source-local 的独立拓扑 meshData，
 * 禁止把 Body triangleRange 套到三角顺序可能不同的 GLB geometry 上。
 */
export class CameraTopologySnapshotSource {
    /** 当前旋转会话的世界矩阵副本，不借用查询器的易变临时对象。 */
    public readonly worldMatrix: THREE.Matrix4;
    /** 快照顶点索引并不保证连续，不能当成数组下标。 */
    private readonly vertices = new Map<number, TopologySnapshotVertex>();
    /** 按索引访问面/边，避免评分完整快照时出现平方级 find 扫描。 */
    private readonly faces = new Map<number, TopologySnapshotFace>();
    /** 边的稳定索引。 */
    private readonly edges = new Map<number, TopologySnapshotEdge>();
    /** 复用读取顶点缓存。 */
    private readonly point = new THREE.Vector3();
    /** 同步范围读取的当前目标。 */
    private boundsTarget: THREE.Box3 | null = null;
    /** 稳定回调，避免为每个面创建坐标闭包。 */
    private readonly expandWorldPoint = (point: THREE.Vector3): void => {
        point.applyMatrix4(this.worldMatrix);
        this.boundsTarget?.expandByPoint(point);
    };

    /**
     * @param mesh 业务显示源，允许脱离场景。
     * @param matrix 已提交的单实例世界矩阵。
     * @param snapshot 权威拓扑快照。
     * @param meshData 导入 Body 完整三角网格；普通结果体不传。
     */
    public constructor(
        public readonly mesh: THREE.Mesh,
        matrix: THREE.Matrix4,
        public readonly snapshot: TopologySnapshot,
        private readonly meshData?: MeshData,
    ) {
        this.worldMatrix = matrix.clone();
        for (const vertex of snapshot.vertices) this.vertices.set(vertex.index, vertex);
        for (const face of snapshot.faces) this.faces.set(face.index, face);
        for (const edge of snapshot.edges) this.edges.set(edge.index, edge);
    }

    /**
     * 读取一个拓扑元素的世界 AABB；普通相机评分不需要创建任何几何资源。
     * @param kind 元素类型。
     * @param index 稳定拓扑索引。
     * @param target 复用结果。
     * @returns 是否存在合法有限范围。
     */
    public getWorldBounds(kind: CameraTopologyElementKind, index: number, target: THREE.Box3): boolean {
        target.makeEmpty();
        this.boundsTarget = target;
        try { if (!this.forEachPoint(kind, index, this.expandWorldPoint)) target.makeEmpty(); }
        finally { this.boundsTarget = null; }
        return !target.isEmpty();
    }

    /**
     * 仅供已确定的调试目标生成局部坐标数组；不调用宿主构建器，也不污染拓扑缓存。
     * @param kind 元素类型。
     * @param index 稳定拓扑索引。
     * @returns 面为三角点，线为折线点，点为单个坐标。
     */
    public createMarkerPositions(kind: CameraTopologyElementKind, index: number): Float32Array {
        const positions: number[] = [];
        if (!this.forEachPoint(kind, index, point => positions.push(point.x, point.y, point.z))) return new Float32Array();
        return new Float32Array(positions);
    }

    /** @param kind 元素类型。 @param index 拓扑索引。 @param visit 同步读取复用局部点。 */
    private forEachPoint(kind: CameraTopologyElementKind, index: number, visit: (point: THREE.Vector3) => void): boolean {
        if (kind === 'feature-face') {
            const face = this.faces.get(index);
            if (!face) return false;
            const position = this.mesh.geometry.getAttribute('position');
            const indices = this.meshData?.indices ?? (this.meshData ? null : this.mesh.geometry.getIndex());
            const vertexCount = this.meshData ? this.meshData.positions.length / 3 : position?.count ?? 0;
            const indexCount = indices instanceof THREE.BufferAttribute ? indices.count : indices?.length ?? vertexCount;
            const [start, end] = face.triangleRange;
            if (!Number.isInteger(start) || !Number.isInteger(end) || start < 0 || end <= start || end * 3 > indexCount) return false;
            for (let offset = start * 3; offset < end * 3; offset++) {
                const vertexIndex = indices instanceof THREE.BufferAttribute ? indices.getX(offset) : indices?.[offset] ?? offset;
                if (!Number.isInteger(vertexIndex) || vertexIndex < 0 || vertexIndex >= vertexCount) return false;
                if (this.meshData) this.point.fromArray(this.meshData.positions, vertexIndex * 3);
                else if (position) this.point.fromBufferAttribute(position, vertexIndex);
                else return false;
                if (!this.isFinitePoint()) return false;
                visit(this.point);
            }
        } else if (kind === 'feature-line') {
            const edge = this.edges.get(index);
            if (!edge) return false;
            if (edge.sampledPoints?.length) {
                for (const point of edge.sampledPoints) {
                    this.point.fromArray(point);
                    if (!this.isFinitePoint()) return false;
                    visit(this.point);
                }
            } else {
                for (const vertexIndex of [edge.vertexAIndex, edge.vertexBIndex]) {
                    const vertex = this.vertices.get(vertexIndex);
                    if (!vertex) return false;
                    this.point.fromArray(vertex.position);
                    if (!this.isFinitePoint()) return false;
                    visit(this.point);
                }
            }
        } else {
            const vertex = this.vertices.get(index);
            if (vertex?.role !== 'topological') return false;
            this.point.fromArray(vertex.position);
            if (!this.isFinitePoint()) return false;
            visit(this.point);
        }
        return true;
    }

    /** @returns 非有限资产坐标不能进入相机范围。 */
    private isFinitePoint(): boolean {
        return Number.isFinite(this.point.x) && Number.isFinite(this.point.y) && Number.isFinite(this.point.z);
    }
}
